<template>
  <div class="attachment-multi-selector">
    <!-- 编辑模式 -->
    <template v-if="!readonly">
      <!-- 仅多选：上方标签列表 -->
      <div v-if="multiple && selectedList.length > 0" class="selected-list">
        <el-popover v-for="(item, index) in selectedList" :key="item.id || index" placement="top-start" trigger="click"
          popper-class="attachment-preview-popover" :value="isAttachmentPreviewOpen(item, index)"
          @input="onAttachmentPreviewInput(item, index, $event)">
          <div class="popover-preview-inner">
            <AttachmentDetail :attachmentType="item.attachmentType" :url="item.url" :bizId="item.bizId"
              @close="closeAttachmentPreview">
            </AttachmentDetail>
          </div>
          <el-tag slot="reference" closable type="info" size="small" class="attachment-tag attachment-tag--clickable"
            @close="handleRemove(item, index)">
            <span class="tag-text active">{{ formatAttachment(item) }}</span>
          </el-tag>
        </el-popover>
      </div>

      <div class="selector-wrapper">
        <Selector :value="selectorValue" :attachmentTypeOpts="attachmentTypeOpts" :multiple="multiple"
          :placeholder="selectorPlaceholder" @input="onSelectorInput" @change="handleSelectorChange" />
        <el-button v-if="hasSelection" type="danger" size="small" plain style="margin-left: 10px"
          @click="handleClearAll">
          清空
        </el-button>
      </div>

      <div v-if="showEmptyHint" class="empty-hint">
        <span class="text-muted">{{ multiple ? "尚未选择附件，请点击上方选择" : "尚未选择附件，请点击选择" }}</span>
      </div>
    </template>

    <!-- 只读模式 -->
    <template v-else>
      <div v-if="multiple && selectedList.length > 0" class="readonly-list">
        <el-popover v-for="(item, index) in selectedList" :key="item.id || index" placement="top-start" trigger="click"
          popper-class="attachment-preview-popover" :value="isAttachmentPreviewOpen(item, index)"
          @input="onAttachmentPreviewInput(item, index, $event)">
          <div class="popover-preview-inner">
            <AttachmentDetail :attachmentType="item.attachmentType" :url="item.url" :bizId="item.bizId"
              @close="closeAttachmentPreview">
            </AttachmentDetail>
          </div>
          <el-tag slot="reference" type="info" size="small" class="attachment-tag readonly attachment-tag--clickable">
            <span class="tag-text">{{ formatAttachment(item) }}</span>
          </el-tag>
        </el-popover>
      </div>
      <span v-else-if="!multiple && singleReadonlyText" class="readonly-text">{{ singleReadonlyText }}</span>
      <span v-else class="readonly-empty">-</span>
    </template>
  </div>
</template>

<script>
import Selector from "./Selector.vue";
import AttachmentDetail from "./AttachmentDetail.vue";
import { mapState } from "vuex";
export default {
  name: "AttachmentMultiSelector",
  components: { Selector, AttachmentDetail },
  props: {
    /**
     * v-model：多选为附件对象数组；单选为单个附件对象或 null
     */
    value: {
      type: [Array, Object],
      default: null
    },
    readonly: {
      type: Boolean,
      default: false
    },
    multiple: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapState({
      attachmentTypeOpts: state => state.enumMap["WORKFLOW_ATTACHMENT_TYPE"] || []
    }),
    hasSelection() {
      if (this.multiple) {
        return this.selectedList.length > 0;
      }
      return !!(this.currentSelection && typeof this.currentSelection === "object");
    },
    showEmptyHint() {
      if (this.multiple) {
        return this.selectedList.length === 0;
      }
      return !this.currentSelection;
    },
    /** 只读 + 单选：一行文案（兼容后端 List 仅一项） */
    singleReadonlyText() {
      if (this.multiple) return "";
      if (this.currentSelection) {
        return this.formatAttachment(this.currentSelection);
      }
      return "";
    },
    /** 传给内层 Selector 的 v-model 等价物：多选 = 全部已选（汇总文案 + 弹窗预选）；单选 = 当前一项 */
    selectorValue() {
      return this.multiple ? this.selectedList : this.currentSelection;
    },
    /** placeholder 随模式区分 */
    selectorPlaceholder() {
      return this.multiple ? "点击选择多个附件" : "点击选择附件";
    }
  },
  data() {
    return {
      currentSelection: null,
      selectedList: [],
      /** 多选标签预览 popover：编辑/只读共用，同时只展开一个 */
      activeAttachmentPreviewKey: null
    };
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(val) {
        if (this.multiple) {
          this.selectedList = Array.isArray(val) ? [...val] : [];
          return;
        }
        this.selectedList = [];
        if (val == null || val === "") {
          this.currentSelection = null;
          return;
        }
        if (Array.isArray(val)) {
          const first = val[0];
          this.currentSelection =
            val.length === 1 && first && typeof first === "object" && !Array.isArray(first)
              ? { ...first }
              : null;
          return;
        }
        if (typeof val === "object") {
          this.currentSelection = { ...val };
        } else {
          this.currentSelection = null;
        }
      }
    }
  },
  methods: {
    formatAttachment(item) {
      if (!item) return "";
      const typeText = this.getAttachmentTypeText(item.attachmentType);
      const title = item.title || "";
      if (!typeText && !title) return "未命名附件";
      if (!typeText) return title;
      if (!title) return typeText;
      return `${typeText} - ${title}`;
    },
    getAttachmentTypeText(type) {
      if (!type) return "";
      const found = this.attachmentTypeOpts.find(
        item => item.value === type || item.value === String(type)
      );
      return found ? found.label : type;
    },
    attachmentPreviewKey(item, index) {
      return item.id != null && item.id !== "" ? item.id : `_idx_${index}`;
    },
    isAttachmentPreviewOpen(item, index) {
      return this.activeAttachmentPreviewKey === this.attachmentPreviewKey(item, index);
    },
    /**
     * 与 el-popover 的受控用法配合：:value 由 isAttachmentPreviewOpen 决定，@input 回传是否展示。
     * 作用：1）记录当前展开的是哪一条，实现「同时只展开一个」；2）关层时若直接 activeKey=null，
     * 在换点另一条时，先关旧再开新，会乱序触发 input(false)；仅当 false 来自「当前正在展开的那条」
     * 才清空，避免误把已切到别处的 activeKey 清掉（与 Selector 里预览列一致）。
     */
    onAttachmentPreviewInput(item, index, visible) {
      const key = this.attachmentPreviewKey(item, index);
      if (visible) {
        this.activeAttachmentPreviewKey = key;
      } else if (this.activeAttachmentPreviewKey === key) {
        this.activeAttachmentPreviewKey = null;
      }
    },
    closeAttachmentPreview() {
      this.activeAttachmentPreviewKey = null;
    },
    /** 单选：同步内层 input 状态（含清空为 null） */
    onSelectorInput(val) {
      if (!this.multiple) {
        this.currentSelection = val;
      }
    },
    handleSelectorChange(val) {
      if (!this.multiple) {
        return this.applySingleChange(val);
      }
      return this.applyMultipleChange(val);
    },
    applySingleChange(val) {
      if (val == null || val === "") {
        this.currentSelection = null;
        this.emitChange();
        return;
      }
      if (
        typeof val === "object" &&
        !Array.isArray(val) &&
        val.id !== undefined &&
        val.id !== null
      ) {
        this.currentSelection = val;
        this.emitChange();
      }
    },
    /** 多选：[] = 输入框清空，等同清空全部已选；非空数组 = 弹窗确定，合并去重追加 */
    applyMultipleChange(val) {
      if (!Array.isArray(val) || val.length === 0) {
        if (this.selectedList.length > 0) {
          this.selectedList = [];
          this.emitChange();
        }
        return;
      }

      let addedCount = 0;
      let duplicateCount = 0;

      for (const item of val) {
        const exists = this.selectedList.some(selected => selected.id === item.id);
        if (exists) {
          duplicateCount++;
          continue;
        }
        this.selectedList.push(item);
        addedCount++;
      }

      if (addedCount > 0) {
        this.emitChange();
        this.$message.success(`已添加 ${addedCount} 个附件`);
      }

      if (duplicateCount > 0) {
        this.$message.warning(`${duplicateCount} 个附件已被选择，已自动跳过`);
      }
    },
    handleRemove(item, index) {
      this.closeAttachmentPreview();
      this.selectedList.splice(index, 1);
      this.emitChange();
    },
    handleClearAll() {
      this.$confirm("确定要清空所有已选择的附件吗？", "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning"
      })
        .then(() => {
          if (this.multiple) {
            this.selectedList = [];
          } else {
            this.currentSelection = null;
          }
          this.closeAttachmentPreview();
          this.emitChange();
          this.$message.success("已清空");
        })
        .catch(() => { });
    },
    emitChange() {
      if (this.multiple) {
        const list = [...this.selectedList];
        this.$emit("input", list);
        this.$emit("change", list);
      } else {
        const v = this.currentSelection;
        this.$emit("input", v);
        this.$emit("change", v);
      }
    }
  },
  beforeDestroy() {
    this.closeAttachmentPreview();
  }
};
</script>

<style lang="less" scoped>
.attachment-multi-selector {
  width: 100%;
}

.selected-list {
  margin-bottom: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.attachment-tag {
  max-width: 300px;

  &.readonly {
    background-color: #f4f4f5;
    border-color: #e9e9eb;
    color: #909399;
  }

  &.attachment-tag--clickable {
    cursor: pointer;
  }

  /deep/ .el-tag__close {
    top: 0;
  }
}

.tag-text {
  display: inline-block;
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
  text-decoration: underline;

  &.active {
    color: #409EFF;
  }
}

.selector-wrapper {
  display: flex;
  align-items: center;
}

.empty-hint {
  margin-top: 8px;
  font-size: 12px;
}

.text-muted {
  color: #909399;
}

.readonly-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.readonly-text {
  color: #606266;
}

.readonly-empty {
  color: #c0c4cc;
}
</style>

<style lang="less">
/* 预览 popper 使用全局样式类（与 Selector 中 attachment-preview-popover 一致） */
.attachment-preview-popover {
  max-height: 72vh;
  overflow-x: hidden;
  overflow-y: hidden;
  padding: 12px 14px;
  box-sizing: border-box;
}

.attachment-preview-popover .popover-preview-inner {
  max-width: 100%;
  overflow-x: hidden;
  box-sizing: border-box;
}
</style>
